import React from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { clinicInfo, testimonials } from '../mockData';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="hero-section relative">
        <div className="hero-background">
          <img
            src="https://customer-assets.emergentagent.com/job_modern-dental-care-9/artifacts/6lig0oxu_cover%20photo.jpeg"
            alt="Dr. Kotecha's Dental Clinic Reception"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="hero-content max-w-3xl">
            <h1 className="hero-title text-white mb-6">
              YOUR SMILE, OUR EXPERTISE
            </h1>
            <p className="body-large text-gray-200 mb-8 max-w-2xl">
              Experience world-class dental care with {clinicInfo.experience} years of excellence. 
              Modern technology meets compassionate care at Kandivali's premier dental clinic.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`tel:${clinicInfo.phone}`}
                className="btn-primary inline-flex items-center justify-center"
              >
                <Phone size={20} className="mr-2" />
                Call to Book
              </a>
              <Link to="/about" className="btn-secondary">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-1 text-white mb-6">
                EXCELLENCE IN DENTAL CARE
              </h2>
              <p className="body-medium text-gray-300 mb-6">
                At Dr. Kotecha's Dental Clinic, we combine decades of expertise with 
                cutting-edge technology to deliver exceptional dental care. Our modern 
                facility features state-of-the-art equipment ensuring precise, comfortable, 
                and effective treatments.
              </p>
              <p className="body-medium text-gray-300 mb-8">
                Led by Dr. Kalpesh Kotecha and Dr. Shwetal Kotecha, our team is committed 
                to providing personalized care that exceeds expectations.
              </p>
              <Link to="/about" className="btn-primary">
                Learn More About Us
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_modern-dental-care-9/artifacts/93q4hs3u_WhatsApp%20Image%202026-04-15%20at%2011.39.34%20AM.jpeg"
                alt="Modern Clinic Interior"
                className="rounded-lg w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="heading-1 text-gray-900 mb-4">PATIENT TESTIMONIALS</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className="text-yellow-500 text-2xl">★</span>
                ))}
              </div>
              <span className="body-medium text-gray-600">4.7 out of 5</span>
            </div>
            <p className="body-medium text-gray-600 max-w-2xl mx-auto">
              Based on 72 Google Reviews - What our patients say about us
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gray-900 p-8 rounded-lg"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-xl">★</span>
                  ))}
                </div>
                <p className="body-small text-gray-300 mb-4">"{testimonial.text}"</p>
                <div className="border-t border-gray-800 pt-4">
                  <p className="body-small text-white font-semibold">{testimonial.name}</p>
                  <p className="caption text-gray-500">{testimonial.date}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://www.google.com/search?q=Dr.+Kotecha%27s+Dental+Clinic+Kandivali+West+Mumbai&rlz=1C1GCEU_enIN819IN819"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              View All 72 Reviews on Google (4.7★)
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="heading-1 text-white mb-6">
            READY FOR A HEALTHIER SMILE?
          </h2>
          <p className="body-large text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your appointment today and experience the difference of expert dental care
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${clinicInfo.phone}`}
              className="btn-primary inline-flex items-center justify-center"
            >
              <Phone size={20} className="mr-2" />
              Call to Book
            </a>
            <a href={`tel:${clinicInfo.phone}`} className="btn-secondary">
              {clinicInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
